import { matchPath } from 'react-router';
import { extractPathName, isExternalUrl } from '../../../utils/string/url.utils';
import { HOUR_IN_MS } from '../../constants/time';
import { generatedRoutes } from '../../routes/generated-routes';
import { doesWindowExist } from '../../utils/dom/dom.utils';

type Route = { [x: string]: any };
type MatchedPath = Route | undefined;

const spaRoutes: Route[] = generatedRoutes.filter((route) => route.disableSPA !== true);

// This check is implemented to force rerender of the page
// if the user is an internal user and has not navigated in the last hour
const forceInternalRefresh = (): boolean => {
	if (window.dataLayer?.isInternalUser) {
		// Get the current time and the last navigation time
		const { performance } = window;

		// If it has been an hour since last nav, don't use SPA routing
		if (performance.now() > HOUR_IN_MS) {
			return true;
		}
	}
	return false;
};

// checks if the app is outdated, i.e. if our client schema version is older than the server schema version
const isAppOutdated = (): boolean => {
	// the schema the client (at time of SSR) was built against
	const ssrSchemaVersion = window['__CLIENT_SCHEMA_VERSION__'];

	// the current schema version of the graph service
	const serverSchemaVersion = window['__SERVER_SCHEMA_VERSION__'];

	// if our schema version is lower then our graph services version, force reload
	return ssrSchemaVersion && serverSchemaVersion && Number(ssrSchemaVersion) < Number(serverSchemaVersion);
};

// if true, forces a full page refresh instead of SPA navigation
const shouldForceFullPageRefresh = (): boolean => {
	return doesWindowExist() && (forceInternalRefresh() || isAppOutdated());
};

/**
 * Searches the routes to find a match and preload those webpack assets
 */
export const preloadComponentForUrl = (url: string) => {
	const matchingRoute = findSPARoute(url);
	return matchingRoute?.component?.preload?.();
};

/**
 * Out of the provided routes will match a url on path
 */
export const findRoute = (url: string, routes: Route[]): MatchedPath => {
	const pathName = extractPathName(url);
	return routes.find((route) => matchPath(route.path, pathName));
};

/**
 * Checks to see if provided url is SPA enabled, i.e. if we do
 * a full page refresh or use react-router
 */
export const findSPARoute = (url: string): MatchedPath => findRoute(url, spaRoutes);

/**
 * Checks if the given URL matches any of the SPA routes.
 * A SPA route is a route that does not have SPA disables via disableSPA property.
 */
export const isSPARoute = (url: string): boolean => {
	return Boolean(!isExternalUrl(url) && !shouldForceFullPageRefresh() && findSPARoute(url));
};
